import { ChevronRight, RefreshCw } from 'lucide-react';
import type { DatabaseSummary, TableStatus } from '../types';
import { formatNumber, formatGigs } from '../utils/formatting';
import { StatusBadge } from './StatusBadge';
import { TableRow } from './TableRow';

interface DatabaseRowProps {
  database: DatabaseSummary;
  tables: TableStatus[];
  isExpanded: boolean;
  onToggle: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
}

export function DatabaseRow({
  database,
  tables,
  isExpanded,
  onToggle,
  onRefresh,
  isRefreshing,
}: DatabaseRowProps) {
  const handleRefreshClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRefresh();
  };

  return (
    <>
      {/* Main database row */}
      <tr
        onClick={onToggle}
        className="border-t border-gray-800/60 hover:bg-gray-800/40 cursor-pointer transition-colors duration-100 group"
      >
        {/* Expand chevron + Workspace */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <ChevronRight
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
            <span className="text-sm text-gray-400">
              {database.workspace_name}
            </span>
          </div>
        </td>

        {/* Database name */}
        <td className="px-4 py-3">
          <span className="text-sm font-medium text-gray-200">
            {database.mirrored_db_name}
          </span>
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          <StatusBadge status={database.mirroring_status} />
        </td>

        {/* Tables count */}
        <td className="px-4 py-3 text-right">
          <span className="text-sm text-gray-300 tabular-nums">
            {database.number_of_tables}
          </span>
        </td>

        {/* Rows */}
        <td className="px-4 py-3 text-right">
          <span className="text-sm text-gray-300 tabular-nums">
            {formatNumber(database.number_of_rows_replicated)}
          </span>
        </td>

        {/* GB */}
        <td className="px-4 py-3 text-right">
          <span className="text-sm text-gray-300 tabular-nums">
            {formatGigs(database.gigs_processed)}
          </span>
        </td>

        {/* Failed */}
        <td className="px-4 py-3 text-right">
          {database.number_of_tables_in_failed_state > 0 ? (
            <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
              {database.number_of_tables_in_failed_state}
            </span>
          ) : (
            <span className="text-sm text-gray-500">0</span>
          )}
        </td>

        {/* Last Sync */}
        <td className="px-4 py-3">
          <span
            className="text-sm text-gray-400"
            title={database.lastSyncTooltip}
          >
            {database.lastSyncRelative}
          </span>
        </td>

        {/* Actions */}
        <td className="px-4 py-3">
          <button
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-700/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-600"
            title={isRefreshing ? 'Refreshing...' : 'Refresh this database'}
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
            />
          </button>
        </td>
      </tr>

      {/* Expanded table details */}
      {isExpanded && tables.length > 0 && (
        <>
          {/* Sub-header */}
          <tr className="bg-gray-950/60">
            <th className="px-4 py-2 pl-12 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Table
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Schema
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rows
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Size
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Sync
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Error
            </th>
          </tr>
          {tables.map((table, index) => (
            <TableRow
              key={`${table.mirrored_database_id}-${table.sourceSchemaName}-${table.sourceTableName}-${index}`}
              table={table}
            />
          ))}
        </>
      )}

      {isExpanded && tables.length === 0 && (
        <tr className="bg-gray-950/60">
          <td colSpan={9} className="px-4 py-6 text-center">
            <span className="text-sm text-gray-500">
              No table data available for this database.
            </span>
          </td>
        </tr>
      )}
    </>
  );
}
